import moment from 'moment';
import { computed, reactive } from 'vue';
import tenants from '@/config/tenants.js';

function getTimeFormat(tenantId) {
  return tenants[tenantId]?.timeFormat || 'HH:mm';
}

function getDayFormat(tenantId) {
  return tenants[tenantId]?.dayFormat || 'DD.MM.YYYY';
}

function getTimeAndDayFormat(tenantId) {
  return `${getTimeFormat(tenantId)} - ${getDayFormat(tenantId)}`;
}

export default function createRttMoment({ locale, tenantId }) {
  const $rttMoment = reactive({
    locale: locale,
    tenantId: tenantId,
    weekStartDay: computed(function getWeekStartDay() {
      try {
        // 2023-05: currently not supported by Firefox
        const intlLocale = new Intl.Locale($rttMoment.locale);
        const firstDay =
          intlLocale.getWeekInfo?.().firstDay ??
          intlLocale.weekInfo?.firstDay ??
          1;
        return firstDay % 7;
      } catch {
        return 1; // fallback 1 = monday
      }
    }),
    country: computed(function (...params) {
      return tenants[$rttMoment.tenantId]?.country.code;
    }),
    region: computed(function () {
      return $rttMoment.locale.split('-')[1] || $rttMoment.country;
    }),
    timeFormat: computed(function () {
      return getTimeFormat(tenantId);
    }),
    timeDayFormat: computed(function () {
      return getTimeAndDayFormat(tenantId);
    }),
    dayTimeFormat: computed(function () {
      return `${getDayFormat(tenantId)} - ${getTimeFormat(tenantId)}`;
    }),
    setLocale: function setLocale(locale) {
      $rttMoment.locale = locale;
      moment.updateLocale(locale, { week: { dow: $rttMoment.weekStartDay } }); // 0 = Sun, 1 = Mon
    },
    setTenantId: function setTenantId(tenantId) {
      $rttMoment.tenantId = tenantId;
    },
    weekdays: computed(function () {
      const weekdays = [];
      const firstDay = $rttMoment.weekStartDay;
      for (let i = firstDay; i < firstDay + 7; i++) {
        const date = new Date('Sun Feb 25 2024');
        date.setDate(date.getDate() + (i % 7));

        const weekDay = {
          shortEngName: date.toLocaleDateString('en-us', {
            weekday: 'short'
          }),
          localeName: date.toLocaleDateString($rttMoment.locale.toLowerCase(), {
            weekday: 'long'
          }),
          shortLocaleName: date.toLocaleDateString(
            $rttMoment.locale.toLowerCase(),
            {
              weekday: 'short'
            }
          )
        };
        weekdays.push(weekDay);
      }
      return weekdays;
    })
  });

  return {
    get $rttMomentInstance() {
      return $rttMoment;
    },
    install(app) {
      app.config.globalProperties.$moment = moment;
      app.config.globalProperties.$rttMoment = $rttMoment;

      app.provide('moment', moment);
      app.provide('rttMoment', $rttMoment);
    }
  };
}
